/**
 * TipTap-JSON als Quellformat aller Nachrichtentexte (Spec 2 / 3.2).
 *
 * Der erlaubte Knoten- und Mark-Vorrat ist hier **abschliessend** definiert und
 * deckt sich mit dem TipTap-StarterKit, das im Admin-UI laeuft. Damit ist der
 * Renderer (`renderTiptapHtml`) total: er kennt jeden Knoten, den das Schema
 * durchlaesst, und muss nie raten.
 *
 * Warum kein TipTap-Renderer auf dem Server: `@tiptap/html` braucht in Node eine
 * DOM-Emulation. Ein eigener Renderer ueber diesem kleinen, geschlossenen
 * Vorrat ist ~100 Zeilen, deterministisch und ohne zusaetzliche Abhaengigkeit.
 * Die API sanitisiert das Ergebnis zusaetzlich (`sanitize-html`) — zweite Linie.
 */
import { z } from 'zod';
export const TIPTAP_MARK_TYPES = ['bold', 'italic', 'underline', 'strike', 'code', 'link'];
export const TIPTAP_NODE_TYPES = [
    'doc',
    'paragraph',
    'text',
    'heading',
    'bulletList',
    'orderedList',
    'listItem',
    'blockquote',
    'codeBlock',
    'hardBreak',
    'horizontalRule',
];
/** Schutz gegen absichtlich tief verschachtelte Dokumente (Stack/CPU). */
export const TIPTAP_MAX_DEPTH = 24;
export const tiptapMarkSchema = z.object({
    type: z.enum(TIPTAP_MARK_TYPES),
    attrs: z.record(z.string(), z.unknown()).optional(),
});
export const tiptapNodeSchema = z.lazy(() => z.object({
    type: z.enum(TIPTAP_NODE_TYPES),
    attrs: z.record(z.string(), z.unknown()).optional(),
    content: z.array(tiptapNodeSchema).optional(),
    marks: z.array(tiptapMarkSchema).optional(),
    text: z.string().optional(),
}));
function depthOf(node, level = 1) {
    if (!node.content || node.content.length === 0)
        return level;
    return Math.max(...node.content.map((child) => depthOf(child, level + 1)));
}
export const tiptapDocSchema = z
    .object({
    type: z.literal('doc'),
    content: z.array(tiptapNodeSchema),
})
    .refine((doc) => doc.content.every((node) => depthOf(node) <= TIPTAP_MAX_DEPTH), {
    message: 'Der Text ist zu tief verschachtelt.',
});
/** Leeres, aber gueltiges Dokument — der Default fuer neue Editoren. */
export function emptyTiptapDoc() {
    return { type: 'doc', content: [{ type: 'paragraph' }] };
}
/** Plaintext → TipTap-JSON. Leerzeilen trennen Absaetze. */
export function tiptapDocFromText(text) {
    const paragraphs = text.split(/\n{2,}/).filter((part) => part.trim().length > 0);
    if (paragraphs.length === 0)
        return emptyTiptapDoc();
    return {
        type: 'doc',
        content: paragraphs.map((paragraph) => ({
            type: 'paragraph',
            content: paragraph.split('\n').flatMap((line, index) => index === 0
                ? [{ type: 'text', text: line }]
                : [{ type: 'hardBreak' }, { type: 'text', text: line }]),
        })),
    };
}
/**
 * Blockknoten, die im Plaintext durch eine Leerzeile getrennt werden.
 * `listItem` fehlt bewusst: seinen Umbruch liefert der Absatz darin.
 */
const PARAGRAPH_BLOCKS = new Set([
    'paragraph',
    'heading',
    'blockquote',
    'codeBlock',
]);
/**
 * TipTap-JSON → Plaintext. Grundlage fuer `body_text`, die Volltextsuche und
 * (ab Phase 2) den Text-Teil ausgehender Mails.
 *
 * Absaetze werden durch eine Leerzeile getrennt, `hardBreak` durch einen
 * einfachen Umbruch — damit ist `tiptapDocFromText` verlustfrei umkehrbar.
 */
export function renderTiptapText(doc) {
    const lines = [];
    let current = '';
    const endLine = () => {
        lines.push(current.trimEnd());
        current = '';
    };
    const walk = (node) => {
        switch (node.type) {
            case 'text':
                current += node.text ?? '';
                return;
            case 'hardBreak':
                endLine();
                return;
            case 'horizontalRule':
                endLine();
                lines.push('---', '');
                return;
            default:
                break;
        }
        for (const child of node.content ?? [])
            walk(child);
        if (PARAGRAPH_BLOCKS.has(node.type)) {
            endLine();
            lines.push('');
        }
        else if (node.type === 'listItem') {
            endLine();
        }
    };
    for (const node of doc.content)
        walk(node);
    if (current.length > 0)
        endLine();
    return lines
        .join('\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}
const HTML_ESCAPES = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
};
function escapeHtml(value) {
    return value.replace(/[&<>"']/g, (char) => HTML_ESCAPES[char] ?? char);
}
/**
 * Nur diese Protokolle ueberleben — `javascript:`, `data:` und relative Ziele
 * fallen raus. Bewusst per Regex statt `new URL()`: `shared` laeuft in Node
 * **und** im Browser und soll keine Umgebungs-Globals brauchen.
 */
const SAFE_LINK_PATTERN = /^(?:https?|mailto):/i;
/** Steuerzeichen wuerden `java\nscript:` an einer Protokollpruefung vorbeischmuggeln. */
// eslint-disable-next-line no-control-regex -- genau darum geht es hier
const CONTROL_CHARACTERS = /[\u0000-\u001f\u007f]/;
function safeHref(value) {
    if (typeof value !== 'string')
        return null;
    const href = value.trim();
    if (href.length === 0 || href.length > 2048)
        return null;
    if (CONTROL_CHARACTERS.test(href))
        return null;
    return SAFE_LINK_PATTERN.test(href) ? href : null;
}
const MARK_TAGS = {
    bold: 'strong',
    italic: 'em',
    underline: 'u',
    strike: 's',
    code: 'code',
};
function renderMarks(html, marks) {
    if (!marks || marks.length === 0)
        return html;
    let result = html;
    for (const mark of marks) {
        if (mark.type === 'link') {
            const href = safeHref(mark.attrs?.['href']);
            // Ohne sicheres Ziel bleibt der Text stehen, der Link verschwindet.
            result = href
                ? `<a href="${escapeHtml(href)}" rel="noopener noreferrer nofollow" target="_blank">${result}</a>`
                : result;
            continue;
        }
        const tag = MARK_TAGS[mark.type];
        result = `<${tag}>${result}</${tag}>`;
    }
    return result;
}
function renderChildren(nodes) {
    return (nodes ?? []).map(renderNode).join('');
}
function renderNode(node) {
    switch (node.type) {
        case 'text':
            return renderMarks(escapeHtml(node.text ?? ''), node.marks);
        case 'hardBreak':
            return '<br>';
        case 'horizontalRule':
            return '<hr>';
        case 'paragraph':
            return `<p>${renderChildren(node.content)}</p>`;
        case 'heading': {
            const raw = node.attrs?.['level'];
            const level = typeof raw === 'number' && raw >= 1 && raw <= 6 ? Math.trunc(raw) : 1;
            return `<h${level}>${renderChildren(node.content)}</h${level}>`;
        }
        case 'bulletList':
            return `<ul>${renderChildren(node.content)}</ul>`;
        case 'orderedList':
            return `<ol>${renderChildren(node.content)}</ol>`;
        case 'listItem':
            return `<li>${renderChildren(node.content)}</li>`;
        case 'blockquote':
            return `<blockquote>${renderChildren(node.content)}</blockquote>`;
        case 'codeBlock':
            return `<pre><code>${escapeHtml(renderTiptapText({ type: 'doc', content: node.content ?? [] }))}</code></pre>`;
        case 'doc':
            return renderChildren(node.content);
        default:
            return renderChildren(node.content);
    }
}
/**
 * TipTap-JSON → HTML. Escaped jeden Textinhalt und gibt ausschliesslich Tags
 * aus dem festen Vorrat oben aus — es gibt keinen Pfad, ueber den Roh-HTML
 * aus dem Dokument in die Ausgabe gelangt.
 */
export function renderTiptapHtml(doc) {
    return renderChildren(doc.content);
}
//# sourceMappingURL=richtext.js.map